import React from "react";
import { Form, Select, Switch, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { type MealsPlanWithCreator } from "@fitness-app/data-models/entities/MealsPlan";
import { getName } from "@fitness-app/data-models/utils/getAuthorName";

import { useTagOptions } from "~/hooks/useTagOptions";
import { type MealsPlanTemplateFilter } from "~/modules/Nutrition/hooks/useMealsPlanTemplateSearch";

interface MealsPlanTemplateFilterFieldsProps {
  value: MealsPlanTemplateFilter;
  onChange: (value: MealsPlanTemplateFilter) => void;
}

/** The two filters above every diet-template picker: tags and "only my templates". */
export const MealsPlanTemplateFilterFields = ({
  value,
  onChange,
}: MealsPlanTemplateFilterFieldsProps): React.ReactElement => {
  const { options: tagsOptions } = useTagOptions("mealsPlanTags");

  return (
    <>
      <Form.Item label="Filtruj przez tagi">
        <Select
          notFoundContent={false}
          options={tagsOptions}
          mode="multiple"
          value={value.tags}
          onChange={(tags) => onChange({ ...value, tags })}
        />
      </Form.Item>
      <Form.Item label="Wyświetl tylko moje szablony">
        <Switch checked={value.onlyMine} onChange={(onlyMine) => onChange({ ...value, onlyMine })} />
      </Form.Item>
    </>
  );
};

type SingleSelectProps = {
  mode?: undefined;
  value?: string;
  onChange?: (value: string | undefined) => void;
};

type MultipleSelectProps = {
  mode: "multiple";
  value?: string[];
  onChange?: (value: string[]) => void;
};

type MealsPlanTemplateSelectProps = (SingleSelectProps | MultipleSelectProps) & {
  plans: MealsPlanWithCreator[];
  onSearch: (value: string) => void;
  loading?: boolean;
  disabled?: boolean;
  placeholder: string;
};

/** A diet-template picker whose options show the author, kcal, preference, macro split, meals and tags. */
export const MealsPlanTemplateSelect = ({
  plans,
  onSearch,
  loading,
  disabled,
  placeholder,
  ...select
}: MealsPlanTemplateSelectProps): React.ReactElement => {
  const { t } = useTranslation("nutrition");

  return (
    <Select
      allowClear
      showSearch
      loading={loading}
      disabled={disabled}
      placeholder={placeholder}
      popupMatchSelectWidth={false}
      onSearch={onSearch}
      optionLabelProp="label"
      // The server already narrowed the list by name; client-side matching would drop the saved ids.
      filterOption={false}
      {...(select as Record<string, unknown>)}
    >
      {plans.map((plan) => (
        <Select.Option value={plan.id} label={plan.name} key={plan.id}>
          <div className="flex flex-col gap-y-1">
            <span>
              {plan.name} {getName(plan.creator, undefined, true)}
            </span>
            <Typography.Text className="text-xs" type="secondary">
              {plan.targetCalories} kcal - {t(`dietaryPreferenceEnum.${plan.dietaryPreference}`)} -{" "}
              {t(`macroSplit.${plan.macroSplit}`)} - {t("mealsPerDay", { count: plan.mealsSchema })}
            </Typography.Text>
            {plan.tags?.length ? (
              <Typography.Text className="text-xs" type="secondary">
                Tagi: {plan.tags.join(", ")}
              </Typography.Text>
            ) : null}
          </div>
        </Select.Option>
      ))}
    </Select>
  );
};
